import { useEffect, useRef } from "react";
import { trackSnapAppEvent } from "@/lib/snap.functions";
import { snapConsent } from "@/lib/snap-policy";

type Platform = "ios" | "android";
const INSTALL_KEY = "inquira.snap.install_id";
const OPTOUT_KEY = "inquira.snap.optout";

function nativePlatform(): Platform | null {
  const cap = (window as unknown as { Capacitor?: { getPlatform?: () => string; isNativePlatform?: () => boolean } }).Capacitor;
  if (!cap?.isNativePlatform?.()) return null;
  const p = cap.getPlatform?.();
  return p === "ios" || p === "android" ? p : null;
}

/**
 * iOS: only when the native app reports App Tracking Transparency as
 * "authorized" (window.InquiraNative.attStatus). The current iOS build does
 * not expose this yet, so iOS sends nothing until a native update adds it.
 * Android also requires explicit native advertising consent; missing = denied.
 */
function trackingAllowed(p: Platform): boolean {
  try {
    if (localStorage.getItem(OPTOUT_KEY) === "1") return false;
  } catch {
    return false;
  }
  const n = (window as unknown as { InquiraNative?: { attStatus?: string; advertisingConsent?: string } }).InquiraNative;
  return snapConsent(p, false, n);
}

function device() {
  const ua = navigator.userAgent;
  const os = /OS (\d+[_.]\d+)/.exec(ua)?.[1]?.replace("_", ".") ?? /Android (\d+(\.\d+)?)/.exec(ua)?.[1];
  return {
    osVersion: os,
    locale: (navigator.language || "en-US").replace("-", "_").slice(0, 16),
    timezone: Intl.DateTimeFormat().resolvedOptions().timeZone?.slice(0, 64),
    screenWidth: Math.round(screen.width),
    screenHeight: Math.round(screen.height),
    density: Math.round((window.devicePixelRatio || 1) * 100) / 100,
    cores: navigator.hardwareConcurrency || undefined,
  };
}

/** Snapchat app events from the iOS/Android app. No-op on the website. */
export function useSnapAppEvents(user: { id: string; created_at?: string; app_metadata?: { provider?: string } } | null) {
  const signupSent = useRef<string | null>(null);
  const opened = useRef(false);

  useEffect(() => {
    const p = nativePlatform();
    if (!p || !user?.id) return;
    let installId: string | null = null;
    try {
      installId = localStorage.getItem(INSTALL_KEY);
      if (!installId) {
        // A web-storage identifier is not proof of a native installation.
        installId = crypto.randomUUID();
        localStorage.setItem(INSTALL_KEY, installId);
      }
    } catch {
      return;
    }
    const base = { platform: p, installId, trackingAllowed: trackingAllowed(p), device: device() };
    if (!opened.current) {
      opened.current = true;
      void trackSnapAppEvent({ data: { ...base, eventName: "APP_OPEN" } }).catch(() => {});
    } else {
      // Persist consent changes without manufacturing another launch.
      void trackSnapAppEvent({ data: { ...base, eventName: "CONSENT_UPDATE" } }).catch(() => {});
    }

    let remove: (() => void) | undefined;
    let disposed = false;
    let active = true;
    void import("@capacitor/app")
      .then(({ App }) =>
        App.addListener("appStateChange", ({ isActive }) => {
          if (isActive && !active) void trackSnapAppEvent({ data: { ...base, trackingAllowed: trackingAllowed(p), eventName: "APP_OPEN" } }).catch(() => {});
          active = isActive;
        }),
      )
      .then((h) => { if (disposed) void h.remove(); else remove = () => void h.remove(); })
      .catch(() => {});
    const consentChanged = () => void trackSnapAppEvent({ data: { ...base, trackingAllowed: trackingAllowed(p), eventName: "CONSENT_UPDATE" } }).catch(() => {});
    window.addEventListener("inquira:tracking-consent-changed", consentChanged);
    return () => { disposed = true; remove?.(); window.removeEventListener("inquira:tracking-consent-changed", consentChanged); };
  }, [user?.id]);

  useEffect(() => {
    const p = nativePlatform();
    if (!p || !user?.id || signupSent.current === user.id) return;
    const registration = (window as unknown as { InquiraNative?: { registrationUserId?: string } }).InquiraNative?.registrationUserId;
    if (registration !== user.id) return; // native registration completion, not ordinary login
    const created = user.created_at ? new Date(user.created_at).getTime() : 0;
    // Only a newly created, confirmed session. Old/recent ordinary logins
    // outside the registration completion window never count as signups.
    if (!Number.isFinite(created) || created > Date.now() || Date.now() - created > 300_000 || !trackingAllowed(p)) return;
    let installId = "unknown-install";
    try {
      installId = localStorage.getItem(INSTALL_KEY) ?? installId;
    } catch {
      /* ignore */
    }
    void trackSnapAppEvent({
      data: {
        eventName: "SIGN_UP",
        platform: p,
        installId,
        trackingAllowed: trackingAllowed(p),
        signUpMethod: user.app_metadata?.provider ?? "email",
        device: device(),
      },
    }).then((r) => { if (r.status === "sent" || r.status === "duplicate") signupSent.current = user.id; }).catch(() => {});
  }, [user?.id]);
}
